"use client"
import { useState, useEffect } from 'react'
import { Button } from "@/components/ui/button"
import { Input } from "@/components/ui/input"
import { PlusIcon, ArrowLeft, Loader2, Download, Search, Calendar, RefreshCw } from "lucide-react"
import { QuotationType, columns } from "./columns"
import { DataTable } from "@/components/data-table"
import QuotationService from '@/api/quotations.service';
import DataRangeService from '@/api/getdatainrange.service';
import * as XLSX from 'xlsx';
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card"
import { Badge } from "@/components/ui/badge"
import { Separator } from "@/components/ui/separator"
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip"

async function getData(setCanLoadMore: any, skip: number, businessId: number): Promise<QuotationType[]> {
  // Fetch data from your API here.
  const response = await QuotationService.getAllQuotations(businessId, skip);
  console.log("getAllQuotations response.data", response.data);
  if (response.data.result === 'invalid') {
    window.location.href = '/logout';
    return [];
  }
  // Notify if exactly 50 quotations are returned
  if (response.data.quotations.length === 50) {
    setCanLoadMore(true); // No more data to load
  }
  return response.data.quotations.map((quotation: any) => ({
    id: quotation.id,
    quotationNumber: quotation.quotationNumber,
    amount: quotation.amount,
    currency: quotation.currency,
    status: quotation.status,
    issueDate: quotation.issueDate,
    expiryDate: quotation.expiryDate,
    dateUpdated: quotation.dateUpdated,
    dateCreated: quotation.dateCreated
  }));
}

async function getDataByDateRange(startDate: Date, endDate: Date, businessId: number): Promise<QuotationType[]> {
  const startYear = startDate.getFullYear();
  const startMonth = startDate.getMonth() + 1; // Months are zero-based
  const startDay = startDate.getDate();
  const endYear = endDate.getFullYear();
  const endMonth = endDate.getMonth() + 1;
  const endDay = endDate.getDate();

  const response = await DataRangeService.getDataUsingDateRangeWithSectionId('quotations', businessId, startYear, startMonth, startDay, endYear, endMonth, endDay);
  if (response.data.result === 'invalid') {
    window.location.href = '/logout';
    return [];
  }
  return response.data.quotations.map((quotation: any) => ({
    id: quotation.id,
    quotationNumber: quotation.quotationNumber,
    amount: quotation.amount,
    currency: quotation.currency,
    status: quotation.status,
    issueDate: quotation.issueDate,
    expiryDate: quotation.expiryDate,
    dateUpdated: quotation.dateUpdated,
    dateCreated: quotation.dateCreated
  }));
}

export default function Quotations() {
  const [data, setData] = useState<QuotationType[]>([]);
  const [isLoading, setIsLoading] = useState(true);

  // Add skip state for pagination
  const [skip, setSkip] = useState(0);
  const [canLoadMore, setCanLoadMore] = useState(false);

  // Date range states
  const [startDate, setStartDate] = useState<Date | null>(null);
  const [endDate, setEndDate] = useState<Date | null>(null);
  const [isFiltering, setIsFiltering] = useState(false);

  const [selectedBusiness] = useState(() => {
    const storedBusiness = typeof window !== 'undefined' ? window.localStorage.getItem('selectedBusiness') : null;
    return storedBusiness ? JSON.parse(storedBusiness) : null;
  });

  useEffect(() => {
    const fetchData = async () => {
      setIsLoading(true);
      const businessId = selectedBusiness.id;
      const result = await getData(setCanLoadMore, skip, businessId);
      setIsLoading(false);
      setData(result);
    };
    fetchData();
  }, [skip]);

  const handleDateRangeSearch = async () => {
    if (startDate && endDate) {
      setIsLoading(true);
      setIsFiltering(true);
      const businessId = selectedBusiness.id;
      const result = await getDataByDateRange(startDate, endDate, businessId);
      setIsLoading(false);
      setData(result);
    }
  };

  const resetFilters = async () => {
    setStartDate(null);
    setEndDate(null);
    setIsFiltering(false);
    setIsLoading(true);
    const businessId = selectedBusiness.id;
    const result = await getData(setCanLoadMore, 0, businessId);
    setSkip(0);
    setIsLoading(false);
    setData(result);
  };

  const exportToExcel = () => {
    const worksheet = XLSX.utils.json_to_sheet(data);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, "Quotations");
    const currentDateTime = new Date().toISOString().replace(/[:.]/g, '-').slice(0, 19);
    XLSX.writeFile(workbook, `Quotations_${currentDateTime}.xlsx`);
  };

  return (
    <Card className="container mx-auto shadow-sm border-0">
      <CardHeader className="pb-4">
        <div className="flex flex-col md:flex-row justify-between items-center">
          <div className="text-center md:text-left">
            <CardTitle className="text-2xl font-bold text-gray-900">Quotations</CardTitle>
            <p className="text-gray-500 mt-1 text-sm">Manage and track your business quotations</p>
          </div>
          <div className="flex flex-col md:flex-row gap-2 mt-4 md:mt-0">
            <TooltipProvider>
              <Tooltip>
                <TooltipTrigger asChild>
                  <Button 
                    onClick={() => window.history.back()} 
                    variant="outline" 
                    size="sm" 
                    className="border-gray-200"
                  >
                    <ArrowLeft className="w-4 h-4" />
                  </Button>
                </TooltipTrigger>
                <TooltipContent>Go back</TooltipContent>
              </Tooltip>
            </TooltipProvider>
            
            <Button 
              onClick={() => window.location.href = '/quotations/create'} 
              className="flex items-center justify-center"
            >
              <PlusIcon className="w-4 h-4 mr-2" /> Create Quotation
            </Button>
          </div>
        </div>
      </CardHeader>
      
      <Separator />
      
      <CardContent className="pt-6">
        <div className="space-y-6">
          <div className="bg-gray-50 p-4 rounded-lg">
            <div className="flex flex-col md:flex-row gap-4 items-end">
              <div className="flex-1">
                <label className="block text-sm font-medium text-gray-700 mb-1" htmlFor="startDate">
                  <Calendar className="w-4 h-4 inline mr-1" /> Issue Date
                </label>
                <Input 
                  id="startDate" 
                  type="date" 
                  value={startDate ? startDate.toISOString().split('T')[0] : ''}
                  onChange={(e) => e.target.value ? setStartDate(new Date(e.target.value)) : setStartDate(null)} 
                  className="border-gray-300 focus:ring-blue-500 focus:border-blue-500 w-full md:w-auto" 
                />
              </div>
              
              <div className="flex-1">
                <label className="block text-sm font-medium text-gray-700 mb-1" htmlFor="endDate">
                  <Calendar className="w-4 h-4 inline mr-1" /> Up until
                </label>
                <Input 
                  id="endDate" 
                  type="date" 
                  value={endDate ? endDate.toISOString().split('T')[0] : ''}
                  onChange={(e) => e.target.value ? setEndDate(new Date(e.target.value)) : setEndDate(null)} 
                  className="border-gray-300 focus:ring-blue-500 focus:border-blue-500 w-full md:w-auto" 
                />
              </div>
              
              <div className="flex gap-2 flex-col md:flex-row">
                <Button 
                  onClick={handleDateRangeSearch} 
                  className="w-full md:w-auto"
                  disabled={!startDate || !endDate || isLoading}
                >
                  {isLoading ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Search className="w-4 h-4 mr-2" />}
                  Filter
                </Button>
                
                {isFiltering && (
                  <Button 
                    onClick={resetFilters} 
                    variant="outline"
                    className="border-gray-300 w-full md:w-auto"
                  >
                    <RefreshCw className="w-4 h-4 mr-2" /> Reset
                  </Button>
                )}
              </div>
            </div>
          </div>
          
          <div className="flex flex-col md:flex-row justify-between items-center">
            <div className="flex-1">
              {isFiltering && startDate && endDate && (
                <Badge variant="outline" className="text-primary bg-primary/10 border-primary/20">
                  Filtered: {startDate.toLocaleDateString()} - {endDate.toLocaleDateString()}
                </Badge>
              )}
              
              {data.length > 0 && (
                <span className="text-sm text-gray-500 ml-2">
                  {data.length} {data.length === 1 ? 'quotation' : 'quotations'} found
                </span>
              )}
            </div>
            
            <div className="flex gap-2 flex-col md:flex-row">
              <Button 
                onClick={exportToExcel} 
                variant="outline" 
                className="border-green-200 text-green-700 hover:bg-green-50 w-full md:w-auto"
                disabled={data.length === 0}
              >
                <Download className="w-4 h-4 mr-2" /> Export to Excel
              </Button>
              
              {canLoadMore && (
                <Button 
                  onClick={() => setSkip(skip + 50)} 
                  variant="outline"
                  className="border-blue-200 text-blue-700 hover:bg-blue-50 w-full md:w-auto"
                  disabled={isLoading}
                >
                  {isLoading ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : null}
                  Load More ({skip} - {skip + 50})
                </Button>
              )}
            </div>
          </div>
          <div className="rounded-lg border border-gray-100 overflow-hidden">
            <DataTable 
              columns={columns} 
              data={data} 
              section="quotations" 
              isLoading={isLoading} 
            />
          </div>
        </div>
      </CardContent>
    </Card>
  );
}
